import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { callTool } from "./api";
import { StatusBlock } from "./components/StatusBlock";
import { BoothNavButton, resolveMapHall } from "./components/BoothNavButton";
import { RatingPill, SegmentPill } from "./components/SegmentPill";
import { WalkPlanButton } from "./components/WalkPlan";
import { accountSupportsWalkPlan } from "./ratings";
import { type CampaignFilters, useRatingBoard } from "./campaignFilters";
import { EVENT_KEY } from "./constants";
import { formatNotePreview, formatOtherRatings, ratingLineage } from "./ratings";
import type { CompanyBrowserTarget } from "./company-browser/types";

interface RatingsBoardProps {
  eventKey?: string;
  actor: string;
  campaignKey?: string;
  filters: CampaignFilters;
  reloadKey?: number;
  compact?: boolean;
  selectedAccountId?: string | null;
  onOpenCompany?: (target: CompanyBrowserTarget) => void;
  onGoToBooth?: (booth: string, label: string, hall?: string) => void;
  sharedBoothIds?: ReadonlySet<string>;
}

function segmentSelectValue(row: { segment: string | null }): string {
  return row.segment?.trim() || "";
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest("button, select, a, input, textarea, label, .segment-pill-wrap"))
  );
}

export function RatingsBoard({
  eventKey = EVENT_KEY,
  actor,
  campaignKey,
  filters,
  reloadKey,
  compact = false,
  selectedAccountId,
  onOpenCompany,
  onGoToBooth,
  sharedBoothIds,
}: RatingsBoardProps) {
  const { board, rows, loadMore, applyLocalTag, hasMore, loadingMore, viewer } = useRatingBoard(
    eventKey,
    actor,
    filters,
    200,
    campaignKey,
    true,
    { reloadKey, scopeToEvent: false },
  );
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const selectedRowRef = useRef<HTMLTableRowElement | null>(null);
  const collapseAnchorRef = useRef<number | null>(null);
  const wasCompactRef = useRef(compact);
  const [openMenu, setOpenMenu] = useState<{ accountId: string; field: "segment" | "rating" } | null>(null);
  const closeMenu = useCallback(() => setOpenMenu(null), []);

  useLayoutEffect(() => {
    const collapsing = compact && !wasCompactRef.current;
    wasCompactRef.current = compact;
    const anchor = collapseAnchorRef.current;
    const row = selectedRowRef.current;
    const scroller = tableWrapRef.current;
    if (!collapsing || anchor == null || !row || !scroller) {
      if (!collapsing) collapseAnchorRef.current = null;
      return;
    }
    const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollTop = Math.max(0, Math.min(max, scroller.scrollTop + (row.getBoundingClientRect().top - anchor)));
    collapseAnchorRef.current = null;
  }, [compact, selectedAccountId]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, rows.length]);

  async function patchTag(accountId: string, patch: Record<string, unknown>) {
    await callTool("account_tag_put", {
      account_id: accountId,
      user: viewer,
      actor,
      lineage: ratingLineage(),
      ...patch,
    });
    applyLocalTag(accountId, patch);
  }

  async function patchSegment(accountId: string, segment: string, prior: Record<string, unknown> | null | undefined) {
    if (!segment) return;
    let current = { ...(prior ?? {}) };
    try {
      const got = await callTool<{ classification?: Record<string, unknown> | null }>("classification_get", {
        account_id: accountId,
      });
      if (got.classification && typeof got.classification === "object") {
        current = got.classification;
      }
    } catch {
      // First classification for this account, or get is unavailable — upsert with what we have.
    }
    await callTool("classification_upsert", {
      account_id: accountId,
      classification: { ...current, segment_primary: segment },
      actor,
      lineage: ratingLineage(),
      source: "account-board",
    });
    applyLocalTag(accountId, { segment });
  }

  const tableState =
    board.status === "ready" && rows.length === 0
      ? { status: "empty" as const, message: "No accounts match these filters." }
      : board;

  return (
    <section className="campaign-builder">
      {tableState.status !== "ready" && rows.length === 0 ? (
        <StatusBlock state={tableState} loadingLabel="Loading account board…" />
      ) : (
        <>
        <div ref={tableWrapRef} className={`rating-table-wrap${compact ? " is-compact" : ""}`}>
          <table className={`rating-table${compact ? " is-compact" : ""}`}>
            <thead>
              <tr>
                <th />
                <th className="company-cell">Company</th>
                <th className="col-compact">Booth</th>
                {compact ? null : (
                  <>
                    <th className="col-compact col-segment">Segment</th>
                    <th className="col-compact col-walk">Walk</th>
                    <th className="col-compact">Contacts</th>
                    <th className="col-compact">AI score</th>
                    <th className="col-compact">My rating</th>
                    <th>Other ratings</th>
                    <th className="notes-cell">Notes</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const segment = segmentSelectValue(row);
                const selected = selectedAccountId === row.account_id;
                return (
                  <tr
                    key={row.account_id}
                    ref={selected ? selectedRowRef : undefined}
                    className={[onOpenCompany ? "clickable-row" : "", selected ? "selected" : ""]
                      .filter(Boolean)
                      .join(" ") || undefined}
                    aria-current={selected ? "true" : undefined}
                    onClick={(event) => {
                      if (!onOpenCompany || isInteractiveTarget(event.target)) return;
                      if (!compact) {
                        collapseAnchorRef.current = event.currentTarget.getBoundingClientRect().top;
                      }
                      onOpenCompany({ account_id: row.account_id });
                    }}
                  >
                  <td>
                    <button
                      type="button"
                      className={`star ${row.my_favorite ? "on" : ""}`}
                      aria-label="Favorite"
                      onClick={() => void patchTag(row.account_id, { favorite: !row.my_favorite })}
                    >
                      {row.my_favorite ? "★" : "☆"}
                    </button>
                  </td>
                  <td className="company-cell">
                    <strong>{row.name}</strong>
                    {compact ? null : <div className="muted">{row.domain}</div>}
                  </td>
                  <td className="col-compact">
                    <BoothNavButton
                      booth={row.booth}
                      shared={Boolean(row.booth && sharedBoothIds?.has(row.booth))}
                      onClick={
                        onGoToBooth
                          ? (booth) => onGoToBooth(booth, row.name ?? booth, resolveMapHall(row.hall, booth))
                          : undefined
                      }
                    />
                  </td>
                  {compact ? null : (
                    <>
                  <td className="col-compact col-segment">
                    <SegmentPill
                      value={segment}
                      open={openMenu?.accountId === row.account_id && openMenu.field === "segment"}
                      onOpen={() => setOpenMenu({ accountId: row.account_id, field: "segment" })}
                      onClose={closeMenu}
                      onSelect={(next) => void patchSegment(row.account_id, next, row.classification)}
                    />
                  </td>
                  <td className="col-compact col-walk">
                    {accountSupportsWalkPlan(row.account_types) ? (
                      <WalkPlanButton
                        on={Boolean(row.my_owner)}
                        showLabel={false}
                        onToggle={() => void patchTag(row.account_id, { owner: !row.my_owner })}
                      />
                    ) : null}
                  </td>
                  <td className="col-compact">{row.contact_count}</td>
                  <td className="col-compact">{row.ai_rating ?? "—"}</td>
                  <td className="col-compact">
                    <RatingPill
                      value={row.my_view ?? ""}
                      open={openMenu?.accountId === row.account_id && openMenu.field === "rating"}
                      onOpen={() => setOpenMenu({ accountId: row.account_id, field: "rating" })}
                      onClose={closeMenu}
                      onSelect={(next) => void patchTag(row.account_id, { user_view: next || "none" })}
                    />
                  </td>
                  <td className="note-cell">{formatOtherRatings(row.other_ratings, viewer) || "—"}</td>
                  <td className="notes-cell">{formatNotePreview(row.notes?.body)}</td>
                    </>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
          <div ref={sentinelRef} className="rating-scroll-sentinel" />
          {loadingMore ? <p className="muted rating-scroll-status">Loading more…</p> : null}
          {!hasMore && rows.length ? <p className="muted rating-scroll-status">End of list</p> : null}
        </div>
        </>
      )}
    </section>
  );
}
